import Grid from '@mui/material/Grid';
import type { SxProps } from '@mui/material/styles';
import { TextField } from '@openx/components-core/lib/TextField/TextField.tsx';
import type { CustomVariablesFormState } from '@openx/types/targeting/advancedTargeting.ts';
import { contextualSegmentPrefix } from '@openx/types/targeting/contextualSegments.ts';
import type { CustomVariablesData } from '@openx/types/targeting/customVariables.ts';
import { ComparisonType } from '@openx/types/targeting/targetingValuesTypes.ts';
import find from 'lodash/find';
import { type JSX, type KeyboardEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  dropdownOptions,
  useCustomVariablesDropdownLabels,
  useCustomVariablesPlaceholder,
} from '../../../CustomVariables/constants.ts';
import { ComparisonTypeDropdown } from '../../../shared/ComparisonTypeDropdown/ComparisonTypeDropdown.tsx';
import { DrawerTopBox } from '../../../shared/TargetingDrawer/DrawerTopBox.tsx';

type CustomVariablesTopBoxProps = {
  items: CustomVariablesFormState;
  onEnter: (customVariables: CustomVariablesData) => void;
  sx?: SxProps;
  /** Narrows the box to fit next to the targeting type selector. */
  besideTypeSelector?: boolean;
};

type ErrorText = {
  customAttribute: string | null;
  customValue: string | null;
};

export const CustomVariablesTopBox = ({
  items,
  onEnter,
  sx,
  besideTypeSelector = false,
}: CustomVariablesTopBoxProps): JSX.Element => {
  const { t } = useTranslation();
  const mapTypeToPlaceholder = useCustomVariablesPlaceholder();
  const labels = useCustomVariablesDropdownLabels();

  const [comparisonType, setComparisonType] = useState<ComparisonType | null>(null);
  const [customAttributeInput, setCustomAttributeInput] = useState<string>('');
  const [customValueInput, setCustomValueInput] = useState<string>('');
  const [errorText, setErrorText] = useState<ErrorText>({ customAttribute: null, customValue: null });

  const onKeyPress = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && comparisonType) {
        const attr = customAttributeInput.trim();
        const val = customValueInput.trim();

        const alreadyExists = find(items.custom, { attr, op: comparisonType, val });

        if (val === '' || attr === '') {
          const attrError = attr === '' ? t('This field cannot be empty.') : null;
          const valError = val === '' ? t('This field cannot be empty.') : null;

          setErrorText({
            customAttribute: attrError,
            customValue: valError,
          });
          return;
        }

        if (attr.startsWith(contextualSegmentPrefix)) {
          setErrorText({
            customAttribute: t('Use Contextual Segments section to set contextual variables.'),
            customValue: null,
          });
          return;
        }

        if (alreadyExists) {
          const errorMessage = t('This custom variable is already on the list.');

          setErrorText({
            customAttribute: errorMessage,
            customValue: errorMessage,
          });
          return;
        }
        onEnter({ op: items.op, val: [...items.custom, { attr, op: comparisonType, val }] });
        setCustomAttributeInput('');
        setCustomValueInput('');
      }

      setErrorText({ customAttribute: null, customValue: null });
    },
    [items, comparisonType, onEnter, t, customValueInput, customAttributeInput]
  );

  const placeholder = mapTypeToPlaceholder[comparisonType ?? mapTypeToPlaceholder[ComparisonType.EQUALS]];

  return (
    <Grid
      size={besideTypeSelector ? 8 : 12}
      sx={{
        flexGrow: 1,
        ...(besideTypeSelector && { paddingLeft: '0.8rem' }),
      }}
    >
      <DrawerTopBox sx={sx}>
        <Grid
          container
          spacing={1}
          sx={{
            flexGrow: 1,
          }}
        >
          <Grid size={3}>
            <TextField
              label={t('Type')}
              fullWidth
              value={customAttributeInput}
              onChange={e => {
                setCustomAttributeInput(e.target.value);
              }}
              onKeyUp={onKeyPress}
              placeholder={t('Enter a value')}
              error={!!errorText.customAttribute}
              helperText={errorText.customAttribute}
              data-test="custom-variables-custom-attribute-input"
            />
          </Grid>

          <Grid size={3}>
            <ComparisonTypeDropdown
              dataTest="custom-variables-comparison-options"
              labels={labels}
              options={dropdownOptions}
              value={comparisonType}
              sx={{ marginTop: 2 }}
              onChange={setComparisonType}
            />
          </Grid>

          <Grid size={6}>
            <TextField
              label={t('Value')}
              value={customValueInput}
              onChange={e => setCustomValueInput(e.target.value)}
              onKeyUp={onKeyPress}
              fullWidth
              placeholder={placeholder}
              error={!!errorText.customValue}
              helperText={errorText.customValue}
              data-test="custom-variables-custom-value-input"
            />
          </Grid>
        </Grid>
      </DrawerTopBox>
    </Grid>
  );
};
